import * as THREE from 'three';
import { AircraftState } from '../sim/AircraftState';
import { RunwayDef } from '../world/Airport';
import { buildRunway } from './Runway';
import { WeatherState, inCloudFactor } from '../weather/Weather';
import { FitRect, backingResolution } from '../core/Letterbox';
import { GpuProfiler } from '../core/GpuProfiler';

const M_TO_FT = 3.28084;
const VIEW_DOWN_OFFSET = 0.22; // rad (~12.5°): look over the nose

/**
 * The outside visual world: sky, textured ground, runway, and — most importantly
 * for an IFR trainer — distance fog that reproduces the ELITE limited-visibility
 * look. Fog `far` = surface visibility; climbing into the overcast whites out
 * the view. The camera is slaved to the aircraft state each frame.
 */
export class Scene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  private fog: THREE.Fog;
  private ground: THREE.Mesh;
  // Outside-view sky colour — independent of the PFD's artificial horizon
  // colours (src/panel/gauges.ts drawAttitude), which are for the instrument
  // only. This is also the fog/haze tint distant ground fades into: fog and
  // background must share one colour or there's a visible seam where fogged-
  // out ground meets the sky at the horizon. The ground itself is green from
  // its own material/texture up close — it only fades to this blue haze in
  // the distance, same as real atmospheric perspective.
  private skyColor = new THREE.Color(0x5b93c9);
  private readonly gpuProfiler: GpuProfiler;

  constructor(canvas: HTMLCanvasElement, runways: RunwayDef[], groundElevation: number) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    this.gpuProfiler = new GpuProfiler(this.renderer);
    // Pixel ratio is fixed at 1: backingResolution() already computes the
    // DPR-scaled (and UHD-capped) physical pixel size we pass to setSize, so
    // letting the renderer also apply devicePixelRatio would double-scale it.
    this.renderer.setPixelRatio(1);
    this.renderer.setClearColor(this.skyColor);

    this.camera = new THREE.PerspectiveCamera(58, 1, 0.5, 60000);
    this.camera.rotation.order = 'YXZ';

    this.fog = new THREE.Fog(this.skyColor, 1, 1600);
    this.scene.fog = this.fog;

    // Lighting: soft ambient + a hazy "sun".
    this.scene.add(new THREE.HemisphereLight(0xdfeaf2, 0x556b55, 1.1));
    const sun = new THREE.DirectionalLight(0xffffff, 0.6);
    sun.position.set(-0.4, 1, 0.3);
    this.scene.add(sun);

    // Ground plane with a procedural grass texture for motion cues.
    const tex = makeGrassTexture();
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(600, 600);
    this.ground = new THREE.Mesh(
      new THREE.PlaneGeometry(120000, 120000),
      new THREE.MeshStandardMaterial({ map: tex, color: 0x6f8f5a, roughness: 1 }),
    );
    this.ground.rotation.x = -Math.PI / 2;
    // A single flat plane at the active airport's MSL elevation. This is the
    // seam a future terrain model replaces: a tessellated mesh sampling
    // TerrainProvider.elevationAt over a grid, authored in MSL metres (camera
    // Y already == MSL, so no extra transform vs. the physics).
    this.ground.position.y = groundElevation;
    this.scene.add(this.ground);

    // Render every airport's runway at its true NED position / elevation, so
    // additional fields appear in-world where they really are.
    for (const rwy of runways) this.scene.add(buildRunway(rwy));
  }

  /** Move the flat ground plane to a new MSL elevation (e.g. when the active
   * airport changes). A real terrain mesh would make this unnecessary. */
  setGroundElevation(m: number): void {
    this.ground.position.y = m;
  }

  /** Re-derive renderer resolution + camera aspect from a letterboxed FitRect.
   * The backing buffer is sized/capped by backingResolution (UHD cap, DPR);
   * the CSS display size is left alone (already set by applyFitRect on the
   * canvas element), so `updateStyle=false` here is required. */
  resize(rect: FitRect): void {
    const { w, h } = backingResolution(rect);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = rect.width / rect.height;
    this.camera.updateProjectionMatrix();
  }

  /** Extra look angles (radians) from mouse look, applied on top of attitude. */
  lookYaw = 0;
  lookPitch = 0;

  update(state: AircraftState, wx: WeatherState): void {
    // Position: NED -> Three (x=E, y=Up, z=-N). Eye a touch above the CG datum.
    this.camera.position.set(state.pos.e, -state.pos.d + 0.4, -state.pos.n);

    // Attitude -> camera Euler (order YXZ). Heading rotates about +Y by -psi.
    // A fixed downward "over the nose" offset raises the horizon above the
    // glareshield so the ground/runway are visible in the window (cosmetic only;
    // the attitude indicator uses the true pitch independently).
    this.camera.rotation.y = -state.psi + this.lookYaw;
    this.camera.rotation.x = state.theta + this.lookPitch - VIEW_DOWN_OFFSET;
    this.camera.rotation.z = -state.phi;

    // Visibility fog.
    this.fog.far = Math.max(120, wx.visibilityMeters);
    this.fog.near = Math.min(this.fog.far * 0.05, 60);

    // Whiteout when in cloud; blend fog + clear colors accordingly.
    const cloud = inCloudFactor(state.altitude * M_TO_FT, wx);
    const base = new THREE.Color().copy(this.skyColor);
    const white = new THREE.Color(0xeef2f4);
    const col = base.lerp(white, cloud);
    this.fog.color.copy(col);
    this.renderer.setClearColor(col);
    if (cloud > 0.01) {
      // Tighten fog dramatically inside cloud.
      this.fog.far = Math.max(120, wx.visibilityMeters) * (1 - 0.9 * cloud);
    }
  }

  render(): void {
    this.gpuProfiler.poll();
    this.gpuProfiler.begin();
    this.renderer.render(this.scene, this.camera);
    this.gpuProfiler.end();
  }

  /** GPU frame time in ms (rolling average), or null if the browser/GPU
   * doesn't expose EXT_disjoint_timer_query_webgl2. */
  get gpuMs(): number | null {
    return this.gpuProfiler.supported ? this.gpuProfiler.milliseconds : null;
  }
}

/**
 * Procedural ground texture: directional (mowed-field-style) grain plus a
 * couple of gently curving lighter "tramline" streaks — replaces the old
 * flat dot-grid depth cue. Because the tile repeats across the whole ground
 * plane, these large, sparse features (unlike small-scale noise) sweep past
 * the camera at a rate that visibly tracks groundspeed/altitude, and their
 * curvature reads distance/parallax the same way real field lines do.
 */
function makeGrassTexture(): THREE.CanvasTexture {
  const size = 512;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#6f8f5a';
  ctx.fillRect(0, 0, size, size);

  // Directional grain: thin horizontal-ish bands of slightly varying shade,
  // instead of uniform per-pixel noise — reads as a "mowed" streaked field.
  const img = ctx.getImageData(0, 0, size, size);
  for (let y = 0; y < size; y++) {
    const bandShade = Math.sin(y * 0.35) * 10 + (Math.random() - 0.5) * 6;
    for (let x = 0; x < size; x++) {
      const n = bandShade + (Math.random() - 0.5) * 14;
      const i = (y * size + x) * 4;
      img.data[i] = clampByte(img.data[i] + n * 0.6);
      img.data[i + 1] = clampByte(img.data[i + 1] + n);
      img.data[i + 2] = clampByte(img.data[i + 2] + n * 0.4);
    }
  }
  ctx.putImageData(img, 0, 0);

  // A couple of gently curving lighter tramlines, tiling seamlessly by
  // wrapping their endpoints across the edges of the tile.
  ctx.strokeStyle = 'rgba(196,204,150,0.4)';
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  drawWrappedCurve(ctx, size, 0.15, 0.62, 0.85, -0.05);
  ctx.strokeStyle = 'rgba(196,204,150,0.25)';
  ctx.lineWidth = 3;
  drawWrappedCurve(ctx, size, -0.1, 0.3, 0.55, 0.95);

  ctx.strokeStyle = 'rgba(30,45,20,0.35)';
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, size, size);
  return new THREE.CanvasTexture(c);
}

/** A single quadratic-curve streak from (x0,y0) to (x1,y1) — fractions of the
 * tile size, may run outside [0,1] — through a midpoint offset, drawn three
 * times (shifted by ±tileSize) so it tiles seamlessly at the texture edges. */
function drawWrappedCurve(ctx: CanvasRenderingContext2D, size: number, x0: number, y0: number, x1: number, y1: number): void {
  const mx = ((x0 + x1) / 2) * size + (Math.random() - 0.5) * size * 0.3;
  const my = ((y0 + y1) / 2) * size + (Math.random() - 0.5) * size * 0.3;
  for (const dx of [-size, 0, size]) {
    for (const dy of [-size, 0, size]) {
      ctx.beginPath();
      ctx.moveTo(x0 * size + dx, y0 * size + dy);
      ctx.quadraticCurveTo(mx + dx, my + dy, x1 * size + dx, y1 * size + dy);
      ctx.stroke();
    }
  }
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}
